import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { kaltura } from "./client";
import type { PickerOption } from "@/lib/liveavatar/options";

const STALE = 5 * 60 * 1000;

/**
 * Catalog faces. Only visuals with a scene ("Image") background are offered:
 * Kaltura bakes the backdrop into the visual, and a flat "Color" backdrop
 * reads as a cut-out rather than a call. `keepId` keeps a scenario's already
 * saved face in the list even if it is a colour visual.
 */
export function useKalturaVisuals(opts?: { enabled?: boolean; keepId?: string }) {
  const q = useQuery({
    queryKey: ["kaltura", "catalog", "visual"],
    queryFn: () => kaltura.listCatalog({ type: "visual" }),
    staleTime: STALE,
    enabled: opts?.enabled ?? true,
  });
  const keepId = opts?.keepId?.trim();
  const options = useMemo<PickerOption[]>(() => {
    const raw = q.data?.items ?? [];
    return raw
      .filter((i) => i.background === "Image" || (!!keepId && i.item_id === keepId))
      .map((i): PickerOption | null => {
        const id = String(i.item_id || "").trim();
        if (!id) return null;
        return {
          id,
          label: String(i.name || id).trim(),
          preview_url: i.image_url,
        };
      })
      .filter((x): x is PickerOption => x !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [q.data, keepId]);
  return { options, isLoading: q.isLoading, error: q.error as Error | null };
}

/** Catalog voices, optionally filtered by language code (e.g. `en-US`). */
export function useKalturaVoices(opts?: { enabled?: boolean; language?: string }) {
  const q = useQuery({
    queryKey: ["kaltura", "catalog", "voice", opts?.language ?? "all"],
    queryFn: () => kaltura.listCatalog({ type: "voice", language: opts?.language }),
    staleTime: STALE,
    enabled: opts?.enabled ?? true,
  });
  const options = useMemo<PickerOption[]>(() => {
    const raw = q.data?.items ?? [];
    return raw
      .map((i): PickerOption | null => {
        const id = String(i.item_id || "").trim();
        if (!id) return null;
        return {
          id,
          label: String(i.name || id).trim(),
          preview_url: i.image_url,
          sublabel: [i.language, i.description].filter(Boolean).join(" · ") || undefined,
        };
      })
      .filter((x): x is PickerOption => x !== null);
  }, [q.data]);
  const languages = useMemo(() => {
    const set = new Set<string>();
    for (const i of q.data?.items ?? []) if (i.language) set.add(i.language);
    return Array.from(set).sort();
  }, [q.data]);
  return { options, languages, isLoading: q.isLoading, error: q.error as Error | null };
}